// src/api/drawingApi.ts
import axios from "axios";
import {
  DrawingConversionRequest,
  DrawingConversionResponse,
} from "../types/drawingTypes";

/** APIのベースURL */
const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  console.error("環境変数 REACT_APP_API_URL が設定されていません");
}

/** タイムアウト時間（ミリ秒） */
const TIMEOUT = 60000;

/** 最大リトライ回数 */
const MAX_RETRIES = 3;

/** ベースの遅延時間（ミリ秒） */
const BASE_RETRY_DELAY = 1000;

/** APIクライアントの設定 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: TIMEOUT,
});

// リクエストインターセプターの追加
apiClient.interceptors.request.use(
  (config) => {
    console.log("リクエスト送信:", config.url);
    return config;
  },
  (error) => {
    console.error("リクエストエラー:", error);
    return Promise.reject(error);
  }
);

// レスポンスインターセプターの追加
apiClient.interceptors.response.use(
  (response) => {
    console.log("レスポンス受信:", response.status);
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      console.error("APIレスポンスエラー:", {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      });
    }
    return Promise.reject(error);
  }
);

/**
 * 図面変換APIクライアント
 */
export const drawingApi = {
  // region: ファイルアップロード
  /**
   * ファイルをアップロードする
   * @param file アップロードするファイル
   * @returns アップロード結果
   */
  uploadFile: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const response = await apiClient.post(
          "/api/File/uploadDrawingFile",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / (progressEvent.total ?? 100)
              );
              console.log(`アップロード進捗: ${percentCompleted}%`);
            },
          }
        );
        return response.data;
      } catch (err) {
        retryCount++;
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("アップロードに失敗しました");
  },
  // endregion

  // region: ファイル変換
  /**
   * 図面ファイルを変換する
   * @param request 変換リクエスト
   * @returns 変換結果
   */
  convertFile: async (request: DrawingConversionRequest) => {
    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const response = await apiClient.post<DrawingConversionResponse>(
          "/api/FileConversion/ConvertDrawingFile",
          {
            inputFilePath: request.conversionParameters.inputFilePath,
            conversionParameters: {
              ...request.conversionParameters,
            },
          }
        );
        return response.data;
      } catch (err) {
        retryCount++;
        console.error("変換エラー:", err);
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("変換に失敗しました");
  },
  // endregion

  // region: ファイルダウンロード
  /**
   * 変換後のファイルをダウンロードする
   * @param filePath ダウンロードするファイルのパス
   * @returns ファイルのBlob
   */
  downloadFile: async (filePath: string) => {
    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const response = await apiClient.get("/api/File/download", {
          params: { filePath },
          responseType: "blob",
        });
        return response.data;
      } catch (err) {
        retryCount++;
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("ダウンロードに失敗しました");
  },
  // endregion

  // region: プレビュー生成
  /**
   * プレビュー画像を生成する
   * @param filePath プレビュー対象のファイルパス
   * @returns プレビュー画像のBase64データ
   */
  generatePreview: async (filePath: string) => {
    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const response = await apiClient.post("/api/FileConversion/clipboard", {
          inputFilePath: filePath,
          mdlType: "",
          machineToolType: "",
          contourLineDisplayMode: true,
        });
        return response.data.base64ImageData;
      } catch (err) {
        retryCount++;
        console.error("プレビュー生成エラー:", err);
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("プレビュー生成に失敗しました");
  },
  // endregion

  // region: ファイル削除
  /**
   * アップロードされたファイルを削除する
   * @param filePath 削除するファイルのパス
   */
  deleteFile: async (filePath: string) => {
    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        await apiClient.delete("/api/File/delete", {
          params: { filePath },
        });
        return;
      } catch (err) {
        retryCount++;
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("ファイル削除に失敗しました");
  },
  // endregion
};